import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  ChevronRight, Loader2, Dog, MapPin, Phone, Mail, ExternalLink, Star, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import ApisAIFooter from '@/components/ApisAIFooter';
import { getImageUrl } from '@/utils/imageUrl';
import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

const BASE_URL = window.location.origin;

export default function KennelsByCity() {
  const { t } = useTranslation();
  const { breed, city } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [breed, city]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/public/hodowle-miasto/${breed}/${city}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
      </div>
    );
  }

  const { breed: breedName, city: cityName, kennels, nearby_kennels, count, nearby_count, seo } = data || {};

  return (
    <>
      <Helmet>
        <title>{seo?.title || `Hodowle ${breedName} ${cityName}`}</title>
        <meta name="description" content={seo?.description} />
        <link rel="canonical" href={`${BASE_URL}${seo?.canonical}`} />
        {seo?.schema && <script type="application/ld+json">{JSON.stringify(seo.schema)}</script>}
      </Helmet>

      <div className="min-h-screen bg-[#0B0F1A]">
        {/* Nav */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-zinc-900/90 backdrop-blur border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <span className="text-2xl">🐾</span>
              <span className="font-semibold text-xl text-white">ApisAI</span>
            </Link>
            <div className="flex gap-4">
              <Link to="/logowanie">
                <Button variant="ghost" className="text-gray-300">Zaloguj</Button>
              </Link>
              <Link to="/rejestracja">
                <Button className="bg-orange-600 hover:bg-orange-700">Dołącz za darmo</Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="pt-24 pb-12 px-6">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumbs */}
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              {seo?.breadcrumbs?.map((crumb, i) => (
                <span key={i} className="flex items-center gap-2">
                  {i > 0 && <ChevronRight className="w-4 h-4" />}
                  <Link to={crumb.url} className="hover:text-white transition">
                    {crumb.name}
                  </Link>
                </span>
              ))}
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 rounded-full text-orange-300 text-sm mb-4">
                <MapPin className="w-4 h-4" />
                {cityName}
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                {seo?.h1 || `Hodowle ${breedName} w ${cityName}`}
              </h1>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Znajdź sprawdzoną hodowlę {breedName} w swoim mieście. 
                {count > 0 ? ` ${count} zarejestrowanych hodowców z rodowodem.` : ' Brak hodowców w tej lokalizacji.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="pb-20 px-6">
          <div className="max-w-7xl mx-auto">
            {/* Kennels in City */}
            {kennels?.length > 0 ? (
              <div className="mb-12">
                <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Dog className="w-5 h-5 text-orange-400" />
                  Hodowle w {cityName} ({count})
                </h2>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {kennels.map((kennel, i) => (
                    <KennelCard key={kennel.id || i} kennel={kennel} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-16 bg-zinc-800/30 rounded-xl border border-zinc-700/50 mb-12">
                <Dog className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  Brak hodowli {breedName} w {cityName}
                </h3>
                <p className="text-gray-400 mb-6">
                  Nie znaleźliśmy jeszcze hodowców tej rasy w Twoim mieście.
                </p>
                <Link to="/rejestracja">
                  <Button className="bg-orange-600 hover:bg-orange-700">
                    Zarejestruj swoją hodowlę
                  </Button>
                </Link>
              </div>
            )}

            {/* Nearby Kennels */}
            {nearby_kennels?.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-emerald-400" />
                  Hodowle w okolicy ({nearby_count})
                </h2>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {nearby_kennels.map((kennel, i) => (
                    <KennelCard key={kennel.id || i} kennel={kennel} isNearby />
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-16 text-center bg-gradient-to-r from-orange-600/20 to-orange-600/20 rounded-2xl p-8 border border-orange-500/20">
              <h3 className="text-2xl font-bold text-white mb-4">
                Prowadzisz hodowlę {breedName}?
              </h3>
              <p className="text-gray-300 mb-6 max-w-xl mx-auto">
                Dołącz do ApisAI i zwiększ widoczność swojej hodowli w wyszukiwarkach AI. 
                Docieraj do klientów szukających {breedName} w {cityName}.
              </p>
              <Link to="/rejestracja">
                <Button size="lg" className="bg-orange-600 hover:bg-orange-700">
                  Dołącz za darmo
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <ApisAIFooter />
      </div>
    </>
  );
}

function KennelCard({ kennel, isNearby }) {
  const coverImage = getImageUrl(kennel.cover_image || kennel.cover_image_url);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-zinc-800/50 rounded-xl overflow-hidden border transition hover:border-orange-500/50 ${
        isNearby ? 'border-zinc-700/50' : 'border-zinc-700'
      }`}
    >
      {/* Cover Image */}
      <div className="h-40 bg-zinc-900 relative">
        {coverImage ? (
          <img 
            src={coverImage} 
            alt={kennel.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Dog className="w-12 h-12 text-zinc-700" />
          </div>
        )}
        {isNearby && (
          <span className="absolute top-2 right-2 px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
            W okolicy
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-white mb-2 line-clamp-1">
          {kennel.name}
        </h3>
        
        <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
          <MapPin className="w-4 h-4" />
          <span>{kennel.city}{kennel.voivodeship ? `, ${kennel.voivodeship}` : ''}</span>
        </div>

        {/* Breeds */}
        <div className="flex flex-wrap gap-1 mb-4">
          {kennel.breeds?.slice(0, 2).map((breed, i) => (
            <span key={i} className="px-2 py-0.5 bg-orange-500/20 text-orange-300 text-xs rounded-full">
              {breed}
            </span>
          ))}
          {kennel.breeds?.length > 2 && (
            <span className="px-2 py-0.5 bg-zinc-700 text-gray-400 text-xs rounded-full">
              +{kennel.breeds.length - 2}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Link to={`/hodowla/${kennel.slug}`} className="flex-1">
            <Button variant="outline" className="w-full text-sm border-zinc-700 hover:border-orange-500">
              Zobacz profil
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
